import { Head } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import EvidenceForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface EvidenceTypeOption {
  id: number
  name: string
  description: string | null
}

interface EmployeeOption {
  id: number
  name: string
}

interface EvidenceData {
  evidence_type_id: number | null
  evidence_date: string
  quantity: number | null
  filename: string
  notes: string
  user_id: number | null
}

interface Props {
  case_record: CaseRecord
  evidence: EvidenceData
  evidence_types: EvidenceTypeOption[]
  errors?: Record<string, string[]>
  can_report_for_others?: boolean
  employees?: EmployeeOption[]
}

export default function New({
  case_record,
  evidence,
  evidence_types,
  errors,
  can_report_for_others,
  employees,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Evidence", href: `/cases/${case_record.id}/evidence` },
    { title: "New", href: `/cases/${case_record.id}/evidence/new` },
  ]

  const { data, setData, post, processing, transform } = useForm<EvidenceData>({
    evidence_type_id: evidence.evidence_type_id,
    evidence_date: evidence.evidence_date,
    quantity: evidence.quantity,
    filename: evidence.filename,
    notes: evidence.notes,
    user_id: evidence.user_id,
  })

  transform((data) => ({ evidence: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/cases/${case_record.id}/evidence`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Evidence - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Evidence</h1>
        <EvidenceForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Evidence"
          evidence_types={evidence_types}
          can_report_for_others={can_report_for_others}
          employees={employees}
        />
      </div>
    </AppLayout>
  )
}
